import type {CommentBoxHandle} from '@github-ui/comment-box/CommentBox'
import type {ValidationResult} from '@github-ui/entity-validators'
import {validateComment} from '@github-ui/entity-validators'
import {areCharacterKeyShortcutsEnabled} from '@github-ui/hotkey/keyboard-shortcuts-helper'
import {useItemPickersContext} from '@github-ui/item-picker-base/ItemPickersContext'
import {useKeyPress} from '@github-ui/use-key-press'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import type {RefObject} from 'react'
import React, {useCallback, useImperativeHandle, useMemo, useState} from 'react'

import {CLASS_NAMES, withClassSelector} from '../constants/dom-elements'
import {HOTKEYS} from '../constants/hotkeys'
import {VALUES} from '../constants/values'
import {type SelectionContext, selectQuoteFromComment} from '../utils/quotes'

export interface MarkdownComposerRef {
  appendText: (text: string) => void
  setText: (text: string) => void
  focus: () => void
}

interface UseMarkdownBodyArgs {
  commentBoxRef: RefObject<CommentBoxHandle | null>
  markdownComposerRef: React.Ref<MarkdownComposerRef>
  onChange: () => void
  onCancel: () => void
  referenceId: string
  insidePortal: boolean
}

export function useMarkdownBody({
  commentBoxRef,
  markdownComposerRef,
  onChange,
  onCancel,
  referenceId,
  insidePortal,
}: UseMarkdownBodyArgs) {
  const [presavedComment, setPresavedComment, resetPresavedComment] = useSessionStorage<string>(
    VALUES.localStorageKeys.issueNewComment('viewer', referenceId),
    '',
  )
  const [markdownBody, setMarkdownBody] = useState<string>(presavedComment)

  const markdownValidationResult = useMemo<ValidationResult>(() => validateComment(markdownBody), [markdownBody])

  const {anyItemPickerOpen} = useItemPickersContext()

  const changeMarkdownBody = useCallback(
    (newMarkdown: string) => {
      setMarkdownBody(newMarkdown)
      setPresavedComment(newMarkdown)
    },
    [setPresavedComment],
  )

  const handleMarkdownBodyChanged = useCallback(
    (newMarkdown: string) => {
      changeMarkdownBody(newMarkdown)
      onChange()
      if (newMarkdown === '') onCancel()
    },
    [changeMarkdownBody, onCancel, onChange],
  )

  useImperativeHandle(
    markdownComposerRef,
    () => ({
      appendText: (text: string) => {
        handleMarkdownBodyChanged(`${markdownBody}\n\n${text}`)
      },
      setText: (text: string) => {
        handleMarkdownBodyChanged(text)
      },
      focus: () => {
        commentBoxRef.current?.focus()
      },
    }),
    [handleMarkdownBodyChanged, markdownBody, commentBoxRef],
  )

  const singleKeyShortcutIsEnabled = useMemo(() => areCharacterKeyShortcutsEnabled(), [])

  const onFocusShortcutActivated = useCallback(
    (e: KeyboardEvent) => {
      if (!singleKeyShortcutIsEnabled || anyItemPickerOpen()) {
        return
      }

      if (commentBoxRef && commentBoxRef.current) {
        e.preventDefault()

        const current = window.getSelection()
        if (current && current.anchorNode) {
          const selectionContext: SelectionContext = {anchorNode: current.anchorNode, range: current.getRangeAt(0)}
          const container = current.anchorNode.parentElement
          const quotedItem =
            container?.closest(withClassSelector(CLASS_NAMES.issueComment)) ??
            container?.closest(withClassSelector(CLASS_NAMES.issueBody))

          // Checking if we can identify the item the selection was made on
          if (quotedItem && quotedItem instanceof HTMLDivElement) {
            const newValue = selectQuoteFromComment(quotedItem, selectionContext)
            changeMarkdownBody(`${newValue}`)
          }
        }

        commentBoxRef.current.scrollIntoView()
        commentBoxRef.current.focus()
      }
    },
    [singleKeyShortcutIsEnabled, anyItemPickerOpen, changeMarkdownBody, commentBoxRef],
  )

  const resetMarkdownBody = useCallback(() => {
    // resetting the ref will also reset dynamic height and will trigger a state update through onChange
    if (commentBoxRef.current) commentBoxRef.current.reset()
    else setMarkdownBody('')
    // This will remove the key from sessionStorage
    resetPresavedComment()
  }, [commentBoxRef, resetPresavedComment])

  useKeyPress([HOTKEYS.focusCommentComposer], onFocusShortcutActivated, {
    triggerWhenInputElementHasFocus: false,
    triggerWhenPortalIsActive: insidePortal,
  })

  return {handleMarkdownBodyChanged, markdownValidationResult, markdownBody, resetMarkdownBody}
}
